import { Controller } from '@hotwired/stimulus'
import Quill from 'quill'

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }, { size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline'],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean']
]

export default class extends Controller {
  static targets = ['editor', 'input']
  static values = {
    placeholder: String,
    labels: { type: Object, default: {} }
  }

  connect () {
    // After Turbo navigation, turbo:permanent keeps the DOM but Stimulus reconnects.
    // Re-attach to the existing Quill instance instead of re-initializing.
    if (this.editorTarget.classList.contains('ql-container')) {
      this.quill = Quill.find(this.editorTarget)
      this.#applyLabels()
      return
    }

    // Initializing Quill inside a `display: none` container (e.g. a collapsed
    // removable field) produces a broken editor: the toolbar renders but
    // formatting has no effect. Defer initialization until the element is
    // actually visible.
    if (this.#isVisible()) {
      this.#initializeQuill()
    } else {
      this.#observeVisibility()
    }
  }

  #initializeQuill () {
    this.quill = new Quill(this.editorTarget, {
      theme: 'snow',
      placeholder: this.placeholderValue,
      modules: { toolbar: TOOLBAR_OPTIONS }
    })

    this.#applyLabels()
    this.syncInput()
    this.quill.on('text-change', () => this.syncInput())
    // Quill has no native blur event; selection-change with range === null means focus left the editor.
    this.quill.on('selection-change', (range) => {
      if (range === null) this.#dispatchChange()
    })
  }

  #isVisible () {
    return this.editorTarget.offsetParent !== null
  }

  #observeVisibility () {
    this.visibilityObserver = new window.ResizeObserver(() => {
      if (this.quill || !this.#isVisible()) return

      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
      this.#initializeQuill()
    })
    this.visibilityObserver.observe(this.editorTarget)
  }

  syncInput () {
    if (this.#isEmpty()) {
      this.inputTarget.value = ''
      return
    }

    this.inputTarget.value = this.quill.getSemanticHTML()
      .replaceAll('\u00A0', ' ')
      .replaceAll('&nbsp;', ' ')
  }

  #dispatchChange () {
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  #isEmpty () {
    return this.quill.getText().trim().length === 0
  }

  #applyLabels () {
    if (!this.hasLabelsValue) return

    const labels = this.labelsValue
    if (Object.keys(labels).length === 0) return

    this.#applyPickerLabels(labels)

    const toolbar = this.element.querySelector('.ql-toolbar')
    if (!toolbar) return

    this.#setTitle(toolbar, '.ql-bold', labels.bold)
    this.#setTitle(toolbar, '.ql-italic', labels.italic)
    this.#setTitle(toolbar, '.ql-underline', labels.underline)
    this.#setTitle(toolbar, '.ql-list[value="ordered"]', labels.ordered_list)
    this.#setTitle(toolbar, '.ql-list[value="bullet"]', labels.bullet_list)
    this.#setTitle(toolbar, '.ql-clean', labels.clean)
    this.#setTitle(toolbar, '.ql-align .ql-picker-item[data-value=""]', labels.align_left)
    this.#setTitle(toolbar, '.ql-align .ql-picker-item[data-value="center"]', labels.align_center)
    this.#setTitle(toolbar, '.ql-align .ql-picker-item[data-value="right"]', labels.align_right)
    this.#setTitle(toolbar, '.ql-align .ql-picker-item[data-value="justify"]', labels.align_justify)
  }

  #applyPickerLabels (labels) {
    const style = this.element.style

    style.setProperty('--quill-header-normal', this.#quotedContent(labels.normal))
    style.setProperty('--quill-header-1', this.#quotedContent(labels.heading_1))
    style.setProperty('--quill-header-2', this.#quotedContent(labels.heading_2))
    style.setProperty('--quill-header-3', this.#quotedContent(labels.heading_3))
    style.setProperty('--quill-size-normal', this.#quotedContent(labels.normal))
    style.setProperty('--quill-size-small', this.#quotedContent(labels.small))
    style.setProperty('--quill-size-large', this.#quotedContent(labels.large))
    style.setProperty('--quill-size-huge', this.#quotedContent(labels.huge))
  }

  #quotedContent (label) {
    return `"${String(label).replaceAll('"', '\\"')}"`
  }

  #setTitle (toolbar, selector, title) {
    if (!title) return

    toolbar.querySelector(selector)?.setAttribute('title', title)
  }

  disconnect () {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
    }
    if (this.quill) {
      this.quill.off('text-change')
      this.quill.off('selection-change')
      this.quill = null
    }
  }
}
